import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { AdminService } from '../../services/AdminService/admin';
import { AuditEventResponse, AuditEventType, AuditOutcome } from '../../models/audit-event.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { StatusBadge } from '../../shared/components/status-badge/status-badge';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { FilterSelect, FilterOption } from '../../shared/components/filter-select/filter-select';
import { PageSizeSelect } from '../../shared/components/page-size-select/page-size-select';

const EVENT_TYPES: AuditEventType[] = [
  'LOGIN_SUCCEEDED',
  'LOGIN_FAILED',
  'LOGIN_BLOCKED',
  'LOGIN_RATE_LIMITED',
  'API_KEY_AUTH_SUCCEEDED',
  'API_KEY_AUTH_FAILED',
  'API_KEY_CREATED',
  'API_KEY_REVOKED',
  'ACCESS_DENIED',
  'COMPANY_REGISTERED',
  'COMPANY_APPROVED',
  'COMPANY_REJECTED',
  'COMPANY_SUSPENDED',
  'COMPANY_REACTIVATED',
  'USER_CREATED',
  'USER_ENABLED',
  'USER_DISABLED',
  'USER_ROLE_CHANGED',
  'CHAIN_SUBMITTED',
  'CHAIN_CONFIRMED',
  'CHAIN_FAILED',
  'WALLET_TOPPED_UP',
  'WALLET_DEBITED',
  'EXPORT_REQUEST_CREATED',
  'EXPORT_REQUEST_ACCEPTED',
  'EXPORT_REQUEST_DECLINED',
  'EXPORT_REQUEST_TRACE_FAILED',
  'EVENTS_TRACED',
  'COUNTRY_TERM_RULE_UPDATED',
  'GENERAL_TERM_UPDATED',
  'MIDDLEWARE_NOTIFIED',
  'MIDDLEWARE_NOTIFY_FAILED',
];

// Char caps for the fixed-width table columns below — a cell past its cap gets an ellipsis;
// the expanded panel always shows every field's full value regardless of truncation, so Actor
// and Detail (which never appear in the row itself) have no cap of their own.
const COMPANY_MAX = 24;
const TARGET_MAX = 20;

function titleCase(word: string): string {
  return word
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

const TYPE_OPTIONS: FilterOption[] = EVENT_TYPES.map((t) => ({ value: t, label: titleCase(t) }));
const OUTCOME_OPTIONS: FilterOption[] = [
  { value: 'SUCCESS', label: 'Success' },
  { value: 'FAILURE', label: 'Failure' },
];

@Component({
  selector: 'app-audit-events',
  imports: [CommonModule, FormsModule, StatusBadge, ErrorBanner, EmptyState, FilterSelect, PageSizeSelect],
  templateUrl: './audit-events.html',
  styleUrl: './audit-events.scss',
})
export class AuditEvents implements OnInit {
  typeOptions = TYPE_OPTIONS;
  outcomeOptions = OUTCOME_OPTIONS;

  typeFilter: AuditEventType | '' = '';
  outcomeFilter: AuditOutcome | '' = '';

  events = signal<AuditEventResponse[]>([]);
  isLoading = signal(false);
  errorMessage = signal('');
  expandedIds = signal<Set<string>>(new Set());

  readonly pageSizeOptions = [10, 20, 50, 100];
  pageSize = signal(10);
  page = signal(0);
  totalPages = signal(1);
  totalCount = signal(0);

  constructor(private admin: AdminService) {}

  get rangeStart(): number {
    return this.totalCount() === 0 ? 0 : this.page() * this.pageSize() + 1;
  }

  get rangeEnd(): number {
    return Math.min((this.page() + 1) * this.pageSize(), this.totalCount());
  }

  /** Page numbers to render, with -1 as an ellipsis placeholder, e.g. [0, -1, 4, 5, 6, -1, 19]. */
  get pageWindow(): number[] {
    const total = this.totalPages();
    const current = this.page();
    const pages: number[] = [];
    const add = (p: number) => {
      if (pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };

    add(0);
    for (let p = current - 1; p <= current + 1; p++) {
      if (p > 0 && p < total - 1) {
        if (p > pages[pages.length - 1] + 1) {
          add(-1);
        }
        add(p);
      }
    }
    if (total - 1 > pages[pages.length - 1] + 1) {
      add(-1);
    }
    if (total > 1) {
      add(total - 1);
    }
    return pages;
  }

  ngOnInit(): void {
    this.load();
  }

  onFilterChange(): void {
    this.page.set(0);
    this.load();
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
    this.load();
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages() || page === this.page()) {
      return;
    }
    this.page.set(page);
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');

    this.admin
      .listAuditEvents(
        {
          type: this.typeFilter || undefined,
          outcome: this.outcomeFilter || undefined,
        },
        this.page(),
        this.pageSize()
      )
      .subscribe({
        next: (res) => {
          this.isLoading.set(false);
          this.events.set(res.data);
          this.totalPages.set(res.page?.total_pages ?? 1);
          this.totalCount.set(res.page?.count ?? res.data.length);
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading.set(false);
          this.errorMessage.set(extractErrorMessage(err, 'Failed to load audit events.'));
        },
      });
  }

  companyLabel(event: AuditEventResponse): string | null {
    if (!event.companyName) return null;
    return event.companyCountryCode ? `${event.companyName} (${event.companyCountryCode})` : event.companyName;
  }

  targetLabel(event: AuditEventResponse): string | null {
    return event.targetType ? `${event.targetType} ${event.targetId}` : null;
  }

  truncate(text: string | null, max: number): string {
    if (!text) return '—';
    return text.length > max ? text.slice(0, max - 1) + '…' : text;
  }

  isExpanded(id: string): boolean {
    return this.expandedIds().has(id);
  }

  toggleRow(id: string): void {
    const next = new Set(this.expandedIds());
    next.has(id) ? next.delete(id) : next.add(id);
    this.expandedIds.set(next);
  }

  readonly COMPANY_MAX = COMPANY_MAX;
  readonly TARGET_MAX = TARGET_MAX;
}
